/**
 * D11 -- one finding, then who it applies to.
 *
 * The examiner shell rendered one row per OFFENDER, so a check that
 * found thirty services printed thirty near-identical lines and each of
 * them named one. From that an examiner cannot tell whether he is
 * reading one finding or thirty -- and thirty rows saying the same
 * thing is the flood P399 removed from the field exam, arriving in the
 * surface built for the person whose judgement it is.
 *
 * Grouping loses nothing: every name is still listed, under the
 * statement it belongs to.
 */
import type { Said } from "shared/supervisory/checkWords";

export interface CoherenceFlag {
  key: string;
  label: string;
  /** ROUND 15 I7 -- `detail` as catalogued fragments. */
  detail_said?: Said[];
  /** CX6 -- the record's address, so a named row can be opened. */
  entity_kind?: string;
  entity_id?: string;
  entity_name: string;
  detail: string;
  /**
   * D3 (round 8) -- every row the flag counted, not just its first.
   *
   * Some checks emit one flag PER offender, which `groupCoherence`
   * merges by key. Others -- the architecture standards -- already
   * collapse server-side into one flag carrying its whole set, because
   * thirty near-identical rows is the flood P399 removed. That set
   * arrived and was thrown away: the examiner read "30 of 205 breach
   * this standard" beside exactly one name, five times over, with the
   * per-row detail on an operator surface his own token cannot reach.
   */
  members?: CoherenceRecord[];
  truncated?: boolean;
  /** Round 9 E4 -- how many rows the flag counted, capped list aside. */
  member_total?: number;
}

/** One record a flag named. */
export interface CoherenceRecord {
  kind: string;
  id: string;
  name: string;
}

export interface CoherenceGroup {
  key: string;
  label: string;
  /** The statement, taken from the first flag: they share it. */
  detail: string;
  /** ROUND 15 I7 -- the same statement as catalogued fragments. */
  detail_said?: Said[];
  names: string[];
  /** D3 -- true where the server capped the set it sent. Said out
   *  loud, because a list that silently stops is worse than a short
   *  one. */
  truncated: boolean;
  /**
   * CX6 -- the same records, addressable.
   *
   * `names` stays: it is what the row READS as, and every caller that
   * only wants the words should not have to unpack an object to get
   * them. This carries the addresses alongside, for the callers that
   * can open one.
   */
  records: CoherenceRecord[];
  /**
   * Round 9 E4 -- what the capped list is a cap OF.
   *
   * `truncated` said there were more and refused to say how many, so
   * the row had no denominator but the size of the list in front of
   * it: "showing 8 of 12", directly above a sentence saying 30 breach.
   *
   * A flag that collapsed its set server-side knows the real total. A
   * flag emitted per offender does not carry one, and does not need
   * to: merging them by key produces the total by counting.
   */
  member_total: number;
}

export function groupCoherence(flags: CoherenceFlag[]): CoherenceGroup[] {
  const out: CoherenceGroup[] = [];
  const index = new Map<string, CoherenceGroup>();
  // Insertion order preserved: the server ranks these, and re-sorting
  // here would quietly override its ordering.
  // D3 -- the rows a flag carries in `members`, falling back to the
  // single entity it names. A flag that collapsed its own set
  // server-side is the ONLY source of that set; a flag emitted per
  // offender names one and is merged by key below. Both end up as one
  // group listing everything.
  const recordsOf = (f: CoherenceFlag): CoherenceRecord[] => {
    if (f.members && f.members.length > 0) return f.members;
    return f.entity_kind && f.entity_id
      ? [{ kind: f.entity_kind, id: f.entity_id, name: f.entity_name }]
      : [];
  };
  for (const f of flags) {
    const rows = recordsOf(f);
    const existing = index.get(f.key);
    if (existing) {
      // A flag with no address still READS as a name, and dropping it
      // because it cannot be opened would lose the row entirely.
      existing.names.push(
        ...(rows.length > 0 ? rows.map((r) => r.name) : [f.entity_name]),
      );
      existing.records.push(...rows);
      existing.truncated = existing.truncated || !!f.truncated;
      existing.member_total += f.member_total ?? rows.length;
      continue;
    }
    const group: CoherenceGroup = {
      key: f.key,
      label: f.label,
      detail: f.detail,
      ...(f.detail_said ? { detail_said: f.detail_said } : {}),
      names: rows.length > 0 ? rows.map((r) => r.name) : [f.entity_name],
      truncated: !!f.truncated,
      records: rows,
      // A per-offender flag reports no total; its own row IS the
      // count, and the merge above adds the rest.
      member_total: f.member_total ?? (rows.length || 1),
    };
    index.set(f.key, group);
    out.push(group);
  }
  return out;
}
